import type { Messages } from './messages';

export type Locale = 'en' | 'ru';

const STORAGE_KEY = 'aria.admin.locale';

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'en' || value === 'ru';
}

export function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* private mode */
  }
  return 'en';
}

export function writeStoredLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* private mode */
  }
}

export function dateLocale(locale: Locale): string {
  return locale === 'ru' ? 'ru-RU' : 'en-GB';
}

export function formatDateTime(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(dateLocale(locale));
}

export function formatShortDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tag = dateLocale(locale);
  const time = date.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString(tag, { day: 'numeric', month: 'short' })}, ${time}`;
}

export function formatBoardDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(dateLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatListTime(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tag = dateLocale(locale);
  const time = date.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return time;
  return date.toLocaleDateString(tag, { day: 'numeric', month: 'short' });
}

export function labelOf(table: object, key: string | undefined | null): string {
  if (!key) return '';
  const record = table as Record<string, unknown>;
  const direct = record[key];
  if (typeof direct === 'string') return direct;
  const folded = key.toLowerCase();
  for (const [name, value] of Object.entries(record)) {
    if (name.toLowerCase() === folded && typeof value === 'string') return value;
  }
  return key.replaceAll('_', ' ');
}

/** CRM funnel/stage labels: always start with a capital letter. */
export function crmName(table: object, key: string | undefined | null): string {
  const label = labelOf(table, key);
  if (!label) return '';
  const chars = [...label];
  chars[0] = chars[0].toLocaleUpperCase();
  return chars.join('');
}

export function relativeTime(iso: string, copy: Messages): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return copy.common.justNow;
  if (minutes < 60) return copy.common.minutesAgo(minutes);
  const hours = Math.round(minutes / 60);
  if (hours < 48) return copy.common.hoursAgo(hours);
  return copy.common.daysAgo(Math.round(hours / 24));
}
